import Loader from '@/components/Loader';
import { reportError } from '@/utils/reportError';
import React, { ReactNode, Suspense, type FC } from 'react';

function parseStackFrame(line: string): { prefix: string; path: string; lineNum: string; col: string } | null {
  const match = line.match(/^(.*)\((.+):(\d+):(\d+)\)\s*$/);
  if (!match) return null;
  return { prefix: match[1], path: match[2], lineNum: match[3], col: match[4] };
}

function openInVSCode(path: string, line: string, col: string) {
  fetch(`/__open-in-editor?file=${encodeURIComponent(`${path}:${line}:${col}`)}`);
}

const StackTrace: FC<{ stack: string }> = ({ stack }) => {
  const lines = stack.split('\n');
  return (
    <pre
      style={{
        textAlign: 'left',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
        fontSize: '0.75rem',
        maxHeight: '60vh',
        overflowY: 'auto',
        background: '#1e1e2e',
        color: '#cdd6f4',
        padding: '1rem',
        borderRadius: '0.5rem',
        margin: '1rem 0 0',
      }}
    >
      {lines.map((line, i) => {
        const frame = parseStackFrame(line);
        if (frame) {
          return (
            <div key={i}>
              {frame.prefix}
              <span
                onClick={() => openInVSCode(frame.path, frame.lineNum, frame.col)}
                style={{ color: '#89b4fa', textDecoration: 'underline', cursor: 'pointer' }}
                title={`Open in VS Code: ${frame.path}:${frame.lineNum}:${frame.col}`}
              >
                ({frame.path}:{frame.lineNum}:{frame.col})
              </span>
            </div>
          );
        }
        return <div key={i}>{line}</div>;
      })}
    </pre>
  );
};

// A stale chunk usually means a new deploy replaced the hashed asset the
// current tab still references, so the dynamic import 404s.
function isStaleChunkError(error: Error): boolean {
  return Boolean(
    error?.message?.includes('Failed to fetch dynamically imported module') ||
    error?.message?.includes('error loading dynamically imported module') ||
    error?.message?.includes('Importing a module script failed'),
  );
}

class ErrorBoundary extends React.Component<
  { children: ReactNode },
  { hasError: boolean; errorInfo: Error | null; reloading: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, errorInfo: null, reloading: false };
  }

  static getDerivedStateFromError(error: Error) {
    // If this is a stale chunk we can still auto-recover, show a loader instead
    // of flashing the error page before componentDidCatch triggers the reload.
    const willReload = isStaleChunkError(error) && !sessionStorage.getItem('chunk-reload');
    return { hasError: true, errorInfo: null, reloading: willReload };
  }

  componentDidMount() {
    // Children rendered without throwing, so the app (and its chunks) loaded
    // cleanly. Clear the stale-chunk reload guard here — NOT in a parent above
    // this boundary, which would also run while the fallback is showing and
    // defeat the single-reload protection, causing an infinite reload loop.
    if (!this.state.hasError) {
      sessionStorage.removeItem('chunk-reload');
    }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ errorInfo: error });
    console.error('ErrorBoundary caught an error:', error, info);

    if (isStaleChunkError(error)) {
      // Reload once per incident to fetch the new index.html; the guard below
      // prevents an infinite reload loop and is cleared once the app next
      // renders without error (see componentDidMount).
      const hasReloaded = sessionStorage.getItem('chunk-reload');

      if (!hasReloaded) {
        sessionStorage.setItem('chunk-reload', 'true');
        window.location.reload();
      }
      return;
    }

    reportError(error, { extra: { componentStack: info.componentStack } });
  }

  render() {
    if (this.state.reloading) {
      return <Loader />;
    }

    if (this.state.hasError) {
      const error = this.state.errorInfo;
      return (
        <div className="login-page">
          <div className="container-xxl h-100 my-auto">
            <div className="error-page">
              <h2 className="fw-semibold">Something went wrong</h2>
              <h5 className="text-gray-700 fw-normal">
                An unexpected error occurred. Please refresh the page or try again later.
              </h5>
              {import.meta.env.DEV && (
                <>
                  <h5 className="text-gray-700 fw-normal">{error?.message || 'Unexpected error occurred'}</h5>
                  {error?.stack && <StackTrace stack={error.stack} />}
                </>
              )}
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const RouteWrapper: FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ErrorBoundary>
      <Suspense fallback={<Loader />}>{children}</Suspense>
    </ErrorBoundary>
  );
};

export default RouteWrapper;
